import {
  AssignmentOutlined as AssignmentIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  Person as PersonIcon,
  RemoveCircleOutline as MissingIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from "@mui/material";
import { useState } from "react";
import isencaoLicenciamentoConsultaService from "../services/isencaoLicenciamentoConsulta";
import { statusIsencaoEnum, statusIsencaoLabels } from "../utils/constants";
import PagamentosSection, { PAPEL_EMPREENDEDOR } from "./PagamentosSection";

const STATUS_COLORS = {
  0: "default",
  1: "info",
  2: "success",
  3: "error",
  4: "warning",
  5: "info",
  6: "secondary",
  7: "success",
};

function VisualizarIsencaoModal({ open, isencao, onClose }) {
  const [loadingTipo, setLoadingTipo] = useState(null);

  if (!isencao) return null;

  const statusLabel = statusIsencaoLabels[isencao.status] ?? "Desconhecido";
  const statusColor = STATUS_COLORS[isencao.status] ?? "default";
  const documentos = isencao.documentos || [];
  const totalEnviados = documentos.length;
  const totalEsperado = 5;
  const podeBaixarEmitida =
    !!(isencao.isencaoEmitida || isencao.isencaoEmitidaS3Key) &&
    (isencao.status === statusIsencaoEnum.ASSINADA ||
      isencao.assinadoDigitalmente === true);

  const handleVisualizarDocumento = (doc) => {
    setLoadingTipo(doc.tipo);
    try {
      if (!doc.conteudo) {
        throw new Error("Conteúdo do documento não disponível");
      }
      const byteArray = Uint8Array.from(atob(doc.conteudo), (c) => c.charCodeAt(0));
      const blob = new Blob([byteArray], { type: doc.contentType || "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
    } finally {
      setLoadingTipo(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <AssignmentIcon color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={600} lineHeight={1.3}>
              Detalhes da Isenção de Licenciamento
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Informações e documentos do requerimento
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <BusinessIcon sx={{ color: "text.secondary", mt: 0.25, fontSize: 20, flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.5}>
                Empreendimento
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {isencao.empreendimentoNome || "—"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <PersonIcon sx={{ color: "text.secondary", mt: 0.25, fontSize: 20, flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.5}>
                Solicitante
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {isencao.solicitanteNome || "Não especificado"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <AssignmentIcon sx={{ color: "text.secondary", mt: 0.25, fontSize: 20, flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.5}>
                Atividade
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {isencao.atividade || "—"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <CalendarIcon sx={{ color: "text.secondary", mt: 0.25, fontSize: 20, flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.5}>
                Data da Solicitação
              </Typography>
              <Typography variant="body1">
                {isencao.createAt
                  ? new Date(isencao.createAt).toLocaleString("pt-BR")
                  : "—"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 20, flexShrink: 0, display: "flex", justifyContent: "center" }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: `${statusColor === "default" ? "grey.400" : `${statusColor}.main`}`,
                }}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.5}>
                Status
              </Typography>
              <Box>
                <Chip
                  label={statusLabel}
                  color={statusColor}
                  size="small"
                  sx={{ mt: 0.25 }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ px: 3, pt: 2, pb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={0.8}>
              Documentos
            </Typography>
            <Chip
              label={`${totalEnviados} / ${totalEsperado} enviados`}
              size="small"
              color={totalEnviados === totalEsperado ? "success" : "default"}
              variant="outlined"
            />
          </Box>
        </Box>

        <List disablePadding>
          {documentos.map((doc, index) => {
            const isLoading = loadingTipo === doc.tipo;
            const temConteudo = !!doc.conteudo;

            return (
              <ListItem
                key={doc.tipo}
                divider={index < documentos.length - 1}
                sx={{ px: 3, py: 1.25 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {temConteudo ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <MissingIcon color="disabled" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={temConteudo ? 500 : 400} color={temConteudo ? "text.primary" : "text.disabled"}>
                      {doc.label}
                    </Typography>
                  }
                  secondary={
                    temConteudo && doc.sizeBytes ? (
                      <Typography variant="caption" color="text.secondary">
                        {(doc.sizeBytes / 1024).toFixed(2)} KB
                      </Typography>
                    ) : !temConteudo ? (
                      <Typography variant="caption" color="text.disabled">
                        Não disponível
                      </Typography>
                    ) : null
                  }
                />
                {temConteudo && (
                  <ListItemSecondaryAction>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        isLoading ? (
                          <CircularProgress size={12} color="inherit" />
                        ) : (
                          <OpenInNewIcon fontSize="small" />
                        )
                      }
                      disabled={!!loadingTipo}
                      onClick={() => handleVisualizarDocumento(doc)}
                    >
                      {isLoading ? "Abrindo..." : "Abrir"}
                    </Button>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            );
          })}
        </List>

        <Box sx={{ px: 3, pb: 2 }}>
          <PagamentosSection
            solicitacao={isencao}
            papel={PAPEL_EMPREENDEDOR}
            limiteUmBoleto
          />
        </Box>

        {isencao.status === statusIsencaoEnum.INDEFERIDO && isencao.motivoIndeferimento && (
          <Alert severity="error" sx={{ mt: 2, mx: 3, mb: 1 }}>
            <strong>Motivo do Indeferimento:</strong> {isencao.motivoIndeferimento}
          </Alert>
        )}

        {isencao.status === statusIsencaoEnum.AGUARDANDO_ASSINATURA && (
          <Alert severity="info" sx={{ mx: 3, mb: 2 }}>
            Documento emitido. Aguardando assinatura digital do secretário.
            A visualização só estará disponível após a assinatura.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {podeBaixarEmitida && (
          <BaixarIsencaoEmitidaButton isencaoId={isencao.id} />
        )}
        <Button onClick={onClose} variant="outlined" color="inherit">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BaixarIsencaoEmitidaButton({ isencaoId }) {
  const [loading, setLoading] = useState(false);

  const handleBaixar = async () => {
    setLoading(true);
    try {
      const data = await isencaoLicenciamentoConsultaService.baixarDocumentoEmitido(isencaoId);
      const byteArray = Uint8Array.from(atob(data.conteudo), (c) => c.charCodeAt(0));
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      color="success"
      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <OpenInNewIcon />}
      disabled={loading}
      onClick={handleBaixar}
    >
      {loading ? "Abrindo..." : "Baixar Isenção Emitida"}
    </Button>
  );
}

export default VisualizarIsencaoModal;
